import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  House, 
  Users, 
  FileText, 
  Wallet, 
  UserCircle, 
  Headset, 
  Storefront,
  SignOut,
  List,
  X,
  Crown
} from '@phosphor-icons/react';

const navItems = [
  { to: '/dashboard', icon: House, label: 'Dashboard' },
  { to: '/leads', icon: Users, label: 'Enquiry' },
  { to: '/customers', icon: Users, label: 'Customers' },
  { to: '/invoices', icon: FileText, label: 'Invoices' },
  { to: '/payments', icon: Wallet, label: 'Payments' },
  { to: '/market-source', icon: Storefront, label: 'Market Source' },
  { to: '/help', icon: Headset, label: 'Help & Support' },
  { to: '/profile', icon: UserCircle, label: 'Profile' },
];

export const DrawerMenu = () => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const isPremium = localStorage.getItem('is_premium') === 'true';

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMobileOpen(false);
  };

  const handleNavClick = () => {
    setIsMobileOpen(false);
  };

  // Sidebar content - shared between mobile and desktop
  const SidebarContent = ({ isMobile = false }) => (
    <div className="h-full flex flex-col bg-[#1a1a2e]">
      {/* Sidebar Header */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div>
          <h1 className="font-['Chivo'] text-xl font-black text-[#FF5A36]">Designbiz.in</h1>
          <p className="text-xs text-gray-400">Business CRM</p>
        </div>
        {isMobile && (
          <button
            onClick={() => setIsMobileOpen(false)}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="close-drawer"
          >
            <X className="h-5 w-5 text-white" weight="bold" />
          </button>
        )}
      </div>

      {/* User Info */}
      {user && (
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-bold">
              {user.name?.charAt(0).toUpperCase() || 'D'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm truncate">{user.name}</p>
              <p className="text-gray-400 text-xs truncate">@{user.profile_id || user.id?.slice(0, 8)}</p>
            </div>
          </div>
          {isPremium ? (
            <div className="mt-3 flex items-center gap-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 px-3 py-1.5 rounded-full">
              <Crown className="h-4 w-4 text-yellow-500" weight="fill" />
              <span className="text-xs text-yellow-500 font-semibold">Premium</span>
            </div>
          ) : (
            <button 
              onClick={() => { navigate('/premium'); setIsMobileOpen(false); }}
              className="mt-3 w-full flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white px-3 py-2 rounded-lg text-xs font-bold hover:opacity-90 transition-opacity"
            >
              <Crown className="h-4 w-4" weight="fill" />
              Upgrade to Premium
            </button>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="py-4 flex-1 overflow-y-auto">
        <ul className="space-y-1 px-3">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive 
                        ? 'bg-[#FF5A36] text-white' 
                        : 'text-gray-400 hover:bg-white/10 hover:text-white'
                    }`
                  }
                >
                  <Icon className="h-5 w-5" weight="duotone" />
                  <span className="text-sm font-medium">{item.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-white/10">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 rounded-lg w-full text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition-all"
          data-testid="drawer-logout"
        >
          <SignOut className="h-5 w-5" weight="bold" />
          <span className="text-sm font-medium">Logout</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar - Always visible on md and above */}
      <aside className="hidden md:block fixed top-0 left-0 h-full w-64 z-40">
        <SidebarContent />
      </aside>

      {/* Mobile Header with Hamburger */}
      <header className="md:hidden bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setIsMobileOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="menu-toggle"
          >
            <List className="h-6 w-6 text-[#121212]" weight="bold" />
          </button>
          <h1 className="font-['Chivo'] text-xl font-black text-[#FF5A36]">Designbiz.in</h1>
          <div className="w-10" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-50 transition-opacity"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Mobile Drawer */}
      <div 
        className={`md:hidden fixed top-0 left-0 h-full w-72 z-50 transform transition-transform duration-300 ease-in-out ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="drawer-menu"
      >
        <SidebarContent isMobile={true} />
      </div>
    </>
  );
};

export default DrawerMenu;
